import React from 'react';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/team' },
    { label: 'Medical Billing', path: '/medical-billing' },
    { label: 'Credentialing', path: '/credentialing' },
    { label: 'Consulting', path: '/consulting' },
    { label: 'Contact', path: '/contact' }
  ];

  const services = [
    { label: 'Medical Billing', path: '/medical-billing' },
    { label: 'Credentialing Services', path: '/credentialing' },
    { label: 'Practice Management', path: '/consulting' },
    { label: 'Consulting Services', path: '/consulting' },
    { label: 'Revenue Cycle Management', path: '/revenue-cycle-management' }
  ];

  return (
    <footer className="relative bg-gradient-to-br from-navy-800 via-navy-700 to-navy-800 text-gray-300 overflow-hidden" role="contentinfo">
      {/* Background pattern */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle, #40E0D0 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }}
        aria-hidden="true"
      />
      
      {/* Gradient accent at top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-teal-400 to-teal-500" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="space-y-6">
            <div>
              <img
                src="/assets/images/cbs-logo.png"
                alt="Capital Billing Services logo"
                className="h-14 w-auto mb-2"
              />
              <div className="text-lg font-semibold text-teal-400">Capital Billing Services</div>
            </div>
            <p className="text-gray-400 leading-relaxed text-sm">
              A family owned full service management billing company dedicated to meeting 
              the needs of your practice.
            </p>
            <div className="flex space-x-3" aria-label="Social media links">
              <a
                href="https://www.facebook.com/capitalbillingservices"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-teal-500 flex items-center justify-center transition-all duration-300 border border-white/10 hover:border-teal-500"
                aria-label="Visit Capital Billing Services on Facebook"
               
                data-testid="footer-facebook-link"
              >
                <Facebook className="w-5 h-5" aria-hidden="true" />
              </a>
              <a
                href="https://twitter.com/capitalbilling"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-teal-500 flex items-center justify-center transition-all duration-300 border border-white/10 hover:border-teal-500"
                aria-label="Visit Capital Billing Services on Twitter"
               
                data-testid="footer-twitter-link"
              >
                <Twitter className="w-5 h-5" aria-hidden="true" />
              </a>
              <a
                href="https://www.linkedin.com/company/capital-billing-services"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-teal-500 flex items-center justify-center transition-all duration-300 border border-white/10 hover:border-teal-500"
                aria-label="Visit Capital Billing Services on LinkedIn"
               
                data-testid="footer-linkedin-link"
              >
                <Linkedin className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <nav aria-label="Quick links">
            <h3 className="text-white font-semibold text-lg mb-6">
              Quick Links
            </h3>
            <ul className="space-y-0.5">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-teal-400 transition-colors duration-200 text-sm hover:translate-x-1 inline-block transform py-1.5"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Services */}
          <nav aria-label="Our services">
            <h3 className="text-white font-semibold text-lg mb-6">
              Our Services
            </h3>
            <ul className="space-y-0.5">
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to={service.path}
                    className="text-gray-400 hover:text-teal-400 transition-colors duration-200 text-sm hover:translate-x-1 inline-block transform py-1.5"
                  >
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6">
              Contact Us
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 group">
                <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-500/20 transition-colors" aria-hidden="true">
                  <Phone className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Phone</div>
                  <a
                    href="tel:4709449277"
                    className="text-gray-300 hover:text-teal-400 transition-colors duration-200 font-medium"
                    aria-label="Call us at 470-944-9277"
                  >
                    470-944-9277
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3 group">
                <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-500/20 transition-colors" aria-hidden="true">
                  <Mail className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Email</div>
                  <a
                    href="mailto:info@capitalbillingservices.com"
                    className="text-gray-300 hover:text-teal-400 transition-colors duration-200 font-medium text-sm"
                    aria-label="Email us at info@capitalbillingservices.com"
                  >
                    info@capitalbillingservices.com
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3 group">
                <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-500/20 transition-colors" aria-hidden="true">
                  <MapPin className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Location</div>
                  <span className="text-gray-300 font-medium">Atlanta, Georgia</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              &copy; {currentYear} Capital Billing Services, Inc. All rights reserved.
            </div>
            <nav aria-label="Legal links">
              <ul className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm">
                <li>
                  <Link to="/privacy-policy" className="inline-block py-2 text-gray-400 hover:text-teal-400 transition-colors duration-200" data-testid="footer-privacy-policy-link">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="inline-block py-2 text-gray-400 hover:text-teal-400 transition-colors duration-200" data-testid="footer-terms-of-service-link">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/hipaa-compliance" className="inline-block py-2 text-gray-400 hover:text-teal-400 transition-colors duration-200" data-testid="footer-hipaa-compliance-link">
                    HIPAA Compliance
                  </Link>
                </li>
                <li>
                  <Link to="/accessibility" className="inline-block py-2 text-gray-400 hover:text-teal-400 transition-colors duration-200" data-testid="footer-accessibility-link">
                    Accessibility
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};
